import { useState } from 'react';
import { MissingResourceError, ValidationError } from '#api/errors.ts';
import { Code } from '#components/atoms/code/index.ts';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { ConfirmDialog } from '#components/patterns/confirmations/confirm-dialog/ConfirmDialog.tsx';
import type { TagName } from '#dn-types/host.ts';
import { useDeleteTag } from './DeleteTagDialog.queries.ts';

type Props = {
  tagName: TagName;
  onCancel: () => void;
  onDelete: () => void;
};

export function DeleteTagDialog({ tagName, onCancel, onDelete }: Props) {
  const [validationErrors, setValidationErrors] = useState<string[] | null>(null);
  const deleteTag = useDeleteTag();
  // The dialog only needs the tag's name (which we already have as a prop). We deliberately do NOT
  // read the tag via useSuspenseTagQuery: it sits inside TagsList with no local Suspense boundary, so
  // the detail fetch would suspend the whole tags table to a spinner while the confirm dialog loads.

  // Clears out the validation error message, in case the modal is re-opened.
  function closeModal() {
    setValidationErrors(null);
    onCancel();
  }

  return (
    <ConfirmDialog
      title={<div className="truncate">Delete {tagName}?</div>}
      acceptButtonText="Delete"
      onCancel={closeModal}
      onAccept={async () => {
        setValidationErrors(null);

        try {
          await deleteTag.mutateAsync(tagName);
          onDelete();
        } catch (error: unknown) {
          if (error instanceof ValidationError) {
            setValidationErrors(error.errors.map((e) => e.message));
          } else if (error instanceof MissingResourceError) {
            setValidationErrors(['Tag not found, maybe it was deleted already?']);
          } else {
            setValidationErrors(['There was a problem deleting the tag.  Please try again or contact support.']);
          }
        }
      }}
      shouldRestoreFocusViaFocusScope={true}
    >
      <p className="p mb-0">
        Are you sure you want to delete <Code className="ml-6 wrap-anywhere">{tagName}</Code>?
      </p>
      {validationErrors?.length ?
        validationErrors.map((message, index) => (
          // eslint-disable-next-line react/no-array-index-key -- It's fine, these aren't going to move/change
          <ErrorMessage key={index} className={index === 0 ? 'mt-32' : undefined}>
            {message}
          </ErrorMessage>
        ))
      : null}
    </ConfirmDialog>
  );
}
